import { z } from 'zod';

/** How a group runs its tests: one after another, or several side by side. */
export const GROUP_MODES = ['SEQUENTIAL', 'PARALLEL'] as const;
export type GroupMode = (typeof GROUP_MODES)[number];

/** Upper bound offered in the UI; the runner also caps it (RUNNER_MAX_PARALLEL). */
export const MAX_GROUP_PARALLEL = 8;
export const DEFAULT_GROUP_PARALLEL = 3;
export const MAX_GROUP_TESTS = 200;

const testIds = z
  .array(z.string().min(1).max(64))
  .min(1, 'Add at least one test')
  .max(MAX_GROUP_TESTS)
  .refine((ids) => new Set(ids).size === ids.length, 'A test can be in a group only once');

export const groupInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).nullish(),
  mode: z.enum(GROUP_MODES).default('SEQUENTIAL'),
  maxParallel: z.number().int().min(2).max(MAX_GROUP_PARALLEL).default(DEFAULT_GROUP_PARALLEL),
  stopOnFailure: z.boolean().default(false),
  /** Tests in run order. */
  testCaseIds: testIds,
});
export type GroupInput = z.infer<typeof groupInputSchema>;

export const groupUpdateSchema = z.object({
  name: groupInputSchema.shape.name.optional(),
  description: groupInputSchema.shape.description,
  mode: z.enum(GROUP_MODES).optional(),
  maxParallel: z.number().int().min(2).max(MAX_GROUP_PARALLEL).optional(),
  stopOnFailure: z.boolean().optional(),
  testCaseIds: testIds.optional(),
});
export type GroupUpdate = z.infer<typeof groupUpdateSchema>;

/**
 * How a run executes its tests (TestRun.plan), fixed when the run starts so later group edits don't
 * change a run in progress. Runs without a plan run one test at a time and never stop early.
 */
export interface RunPlan {
  mode: GroupMode;
  maxParallel: number;
  stopOnFailure: boolean;
  groupId?: string;
  groupName?: string;
}

export const SEQUENTIAL_PLAN: RunPlan = { mode: 'SEQUENTIAL', maxParallel: 1, stopOnFailure: false };

/** Reads TestRun.plan defensively (older runs have none). */
export function parseRunPlan(value: unknown): RunPlan {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return SEQUENTIAL_PLAN;
  const v = value as Record<string, unknown>;
  const mode: GroupMode = v.mode === 'PARALLEL' ? 'PARALLEL' : 'SEQUENTIAL';
  const maxParallel = mode === 'PARALLEL' && typeof v.maxParallel === 'number' && Number.isInteger(v.maxParallel) ? Math.min(Math.max(v.maxParallel, 1), MAX_GROUP_PARALLEL) : 1;
  return {
    mode,
    maxParallel,
    stopOnFailure: v.stopOnFailure === true,
    ...(typeof v.groupId === 'string' ? { groupId: v.groupId } : {}),
    ...(typeof v.groupName === 'string' ? { groupName: v.groupName } : {}),
  };
}

/** Tests at the same time for a plan, given the runner's own limit. */
export function effectiveParallel(plan: RunPlan, runnerLimit: number): number {
  return plan.mode === 'PARALLEL' ? Math.max(1, Math.min(plan.maxParallel, runnerLimit)) : 1;
}
